import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import OnboardingLayout, { StepCard, StepNav } from "@/pages/onboarding/OnboardingLayout";
import PlanProStep from "@/pages/onboarding/steps/PlanProStep";
import TierStep from "@/pages/onboarding/steps/TierStep";
import BrokerStep from "@/pages/onboarding/steps/BrokerStep";
import BillingStep from "@/pages/onboarding/steps/BillingStep";
import { useOnboarding } from "@/context/OnboardingContext";
import { apiClient, formatApiError, readAuth, writeAuth } from "@/lib/apiClient";

const TOTAL_STEPS = 4;
const STEP_KEYS = ["plan", "tier", "brokers", "billing"];

const validators = {
  plan: (d) => !!d.plan?.cadence,
  tier: (d) => !!d.tier,
  brokers: () => true,
  billing: (d) => {
    const b = d.billing || {};
    const cn = (b.card_number || "").replace(/\s/g, "");
    return !!(
      (b.cardholder || "").trim() &&
      /^\d{15,16}$/.test(cn) &&
      /^\d{2}\/\d{2}$/.test(b.expiry || "") &&
      /^\d{3,4}$/.test(b.cvc || "") &&
      /^\d{5}$/.test(b.zip || "")
    );
  },
};

const ProWizard = () => {
  const { data, setSection, patchSection, savePartial, step, setStep } = useOnboarding();
  const [submitting, setSubmitting] = useState(false);

  const idx = Math.max(0, STEP_KEYS.indexOf(step));
  const currentKey = STEP_KEYS[idx];
  const canContinue = (validators[currentKey] || (() => true))(data);

  const advance = useCallback(
    async (delta) => {
      const nextIdx = Math.max(0, Math.min(STEP_KEYS.length - 1, idx + delta));
      const nextKey = STEP_KEYS[nextIdx];
      setStep(nextKey);
      await savePartial(nextKey);
    },
    [idx, setStep, savePartial]
  );

  const finish = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await apiClient.post("/onboarding/complete", { data });
      const auth = readAuth();
      if (auth && res.user) writeAuth({ ...auth, user: res.user });
      toast.success("Welcome to Judgment Enforcement AI");
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingLayout step={idx + 1} total={TOTAL_STEPS}>
      {currentKey === "plan" && (
        <StepCard width={640} testid="onboarding-card-plan">
          <PlanProStep data={data} patchSection={patchSection} />
          <StepNav
            onBack={() => advance(-1)}
            onContinue={() => advance(1)}
            disableBack
            disableContinue={!canContinue}
          />
        </StepCard>
      )}

      {currentKey === "tier" && (
        <StepCard width={780} testid="onboarding-card-tier">
          <TierStep
            data={data}
            patchSection={(_, v) => setSection("tier", v)}
            accountType="pro"
            debtorCount={99}
          />
          <StepNav onBack={() => advance(-1)} onContinue={() => advance(1)} disableContinue={!canContinue} />
        </StepCard>
      )}

      {currentKey === "brokers" && (
        <StepCard width={720} testid="onboarding-card-brokers">
          <BrokerStep data={data} setSection={setSection} accountType="pro" />
          <StepNav onBack={() => advance(-1)} onContinue={() => advance(1)} />
        </StepCard>
      )}

      {currentKey === "billing" && (
        <StepCard width={560} testid="onboarding-card-billing">
          <BillingStep data={data} patchSection={patchSection} accountType="pro" />
          <StepNav
            onBack={() => advance(-1)}
            onContinue={finish}
            disableContinue={!canContinue}
            loading={submitting}
            continueLabel="Complete Setup"
          />
        </StepCard>
      )}
    </OnboardingLayout>
  );
};

export default ProWizard;
